import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Subscription, of, throwError, timer } from 'rxjs';
import { catchError, finalize, retry } from 'rxjs/operators';

import { CopilotApiService } from '@api/api/copilot.service';
import { CopilotStatusResponse } from '@api/model/copilotStatusResponse';

import { AuthService } from '../../core/services/auth.service';
import { LoggerService } from '../../core/services/logger.service';

/**
 * Runtime copilot status — the operational (env-flip) toggle.
 *
 * Contract: `GET /api/v1/copilot/status` (via the GENERATED
 * `CopilotApiService.copilotStatus()` — GAP-CP-021-B removed the hand-rolled
 * URL + wire-shape copy) returns `{ enabled, model, dailyBudgetTokens,
 * usedTokens }` for ANY authenticated user and is CHEAP (backend reads
 * `CopilotRelayConfig::from_env`, no network / DB). The FE gates copilot panel
 * + palette-entry VISIBILITY on `status().enabled`, so enabling the feature is
 * a backend env flip (`COPILOT_CHAT_ENABLED`) with NO frontend rebuild.
 *
 * FAIL-CLOSED: the signal defaults to `{ enabled: false }` and stays that way on
 * a pending fetch, an HTTP error, or a malformed body — until the FIRST
 * successful probe for the current principal. Only an explicit `enabled: true`
 * from the backend opens the surface. The build-time
 * `environment.features.enableCopilotChat` flag remains the HARD kill-switch and
 * is AND-ed with this (see {@link AgentChatService.enabled}).
 *
 * GAP-CP-021-A — the status is no longer fetched ONCE per principal: a slow
 * poll (plus {@link refresh} calls on RUN_FINISHED / RUN_ERROR / panel open)
 * keeps the budget footer and the `COPILOT_CHAT_ENABLED` flip observable
 * mid-session. A refresh FAILURE after a prior success keeps the last-known
 * status (so a transient 500 can never slam a mid-conversation panel shut) and
 * raises {@link unavailable}, which the panel renders as a muted
 * "Copilot unavailable" hint.
 */

/**
 * GAP-CP-021-B — the FE view of the status, DERIVED from the generated
 * `CopilotStatusResponse` model (never a hand-rolled wire-shape copy): the
 * budget/model fields go optional because pre-first-fetch and fail-closed
 * states have no backend values, but any backend field rename/retype now
 * surfaces as a compile error here instead of silent drift.
 */
export type CopilotStatus = Pick<CopilotStatusResponse, 'enabled'> &
  Partial<Pick<CopilotStatusResponse, 'model' | 'dailyBudgetTokens' | 'usedTokens'>>;

const DARK: CopilotStatus = { enabled: false };
const STATUS_RETRY_COUNT = 2;
const STATUS_RETRY_BASE_DELAY_MS = 250;
/**
 * GAP-CP-021-A — slow background poll cadence. Deliberately lazy (5 min): the
 * eventful refresh triggers (run finish/error, panel open) carry the load;
 * this only bounds how stale a silent `COPILOT_CHAT_ENABLED` flip or budget
 * movement can get for an idle tab.
 */
const STATUS_SLOW_POLL_MS = 5 * 60_000;

@Injectable({ providedIn: 'root' })
export class CopilotStatusService {
  /** GAP-CP-021-B — the GENERATED client (never a hand-rolled URL). */
  private readonly copilotApi = inject(CopilotApiService);
  private readonly auth = inject(AuthService);
  private readonly logger = inject(LoggerService);

  private readonly statusSig = signal<CopilotStatus>(DARK);
  /**
   * GAP-CP-021-A · true when the LAST status fetch failed. While a prior fetch
   * for the same principal succeeded, the last-known status is retained (the
   * panel stays usable) and the UI shows a muted "Copilot unavailable" hint
   * instead of hard-closing the surface on a transient backend blip.
   */
  private readonly unavailableSig = signal(false);
  readonly unavailable = this.unavailableSig.asReadonly();
  /** Whether any fetch has succeeded for the CURRENT principal (fail-closed until then). */
  private hadSuccessForPrincipal = false;
  private inflight: Subscription | null = null;
  /**
   * RF-QA-171 · The principal the current in-flight/last probe belongs to.
   * Lets a transient auth-ready gate (effective-permissions re-hydration of the
   * SAME principal) keep — rather than abort and re-issue — a probe that is
   * still in flight, so a cold load costs one `/copilot/status` request.
   */
  private probedPrincipalId: string | null = null;

  /**
   * GAP-CP-021-A · the session principal the copilot state is scoped to:
   * user id AND tenant id (tenant switches change the budget scope, so they
   * re-probe too). Mirrored by `AgentChatService`'s principal-change effect —
   * keep the two in lockstep.
   */
  private readonly authenticatedPrincipalId = computed(() => {
    if (!this.auth.isAuthenticated() || !this.auth.authReady()) {
      return null;
    }
    const user = this.auth.currentUser();
    if (!user?.id) {
      return null;
    }
    return `${user.id}::${user.tenantId ?? ''}`;
  });
  /** Runtime status. Fail-closed default `{ enabled: false }` until the backend
   * explicitly reports `enabled: true`. */
  readonly status = this.statusSig.asReadonly();

  constructor() {
    effect((onCleanup) => {
      const principalId = this.authenticatedPrincipalId();

      // `/copilot/status` is authenticated. Waiting for authReady prevents the
      // app-bootstrap initializer from issuing a token-less one-shot request
      // before session restoration / login and RBAC hydration have settled.
      if (!principalId) {
        // Fail closed for the duration of the gate.
        this.statusSig.set(DARK);
        this.unavailableSig.set(false);
        this.hadSuccessForPrincipal = false;
        // RF-QA-171 — only a real de-authentication aborts the probe. A
        // transient authReady=false (effective-permissions re-hydration of the
        // same session) keeps it in flight so its answer can land once the
        // same principal is ready again instead of being re-requested.
        if (!this.auth.isAuthenticated()) {
          this.cancelInflight();
          this.probedPrincipalId = null;
        }
        return;
      }

      // Auth signals are reactive: a successful login, session restore, tenant
      // re-hydration, or principal change automatically starts a fresh probe
      // cycle. Cancelling the previous request prevents a stale principal's
      // response from re-enabling the surface after logout or account
      // switching; resetting hadSuccessForPrincipal restores the fail-closed
      // posture for the NEW principal.
      this.statusSig.set(DARK);
      this.unavailableSig.set(false);
      this.hadSuccessForPrincipal = false;
      if (principalId === this.probedPrincipalId && this.inflight !== null) {
        // RF-QA-171 — the SAME principal re-armed while its probe is still in
        // flight: join that request (its response is for this principal).
      } else {
        this.cancelInflight();
        this.probedPrincipalId = principalId;
        // GAP-CP-021-A — immediate (synchronous) probe, then a slow background
        // poll. The first probe stays synchronous so the surface's fail-closed →
        // enabled transition is not delayed behind a macrotask.
        this.refresh();
      }
      const poll = timer(STATUS_SLOW_POLL_MS, STATUS_SLOW_POLL_MS).subscribe(() =>
        this.refresh(),
      );
      // Only the poll clock is torn down here; the in-flight probe is cancelled
      // explicitly above on logout / principal change (RF-QA-171).
      onCleanup(() => poll.unsubscribe());
    });
  }

  /**
   * GAP-CP-021-A · Re-probe `/copilot/status` now. Public so eventful triggers
   * (RUN_FINISHED / RUN_ERROR in `AgentChatService`, panel open) can refresh
   * the budget footer and observe a `COPILOT_CHAT_ENABLED` flip without
   * waiting for the slow poll. No-op while unauthenticated.
   *
   * RF-QA-171 — single-flight: a call made while a probe is already in flight
   * joins it instead of aborting and re-issuing (one request, still in order),
   * so overlapping triggers (panel open + run finished, or the cold-load
   * auth-ready transitions) cost one `/copilot/status` round-trip.
   */
  refresh(): void {
    if (!this.authenticatedPrincipalId()) {
      return;
    }
    if (this.inflight !== null) {
      // An eventful trigger (run finished, panel opened) arrived mid-probe:
      // the in-flight answer may predate it, so re-probe ONCE when it settles
      // rather than dropping the refresh until the 5-minute poll.
      this.refreshQueued = true;
      return;
    }
    const probe = this.fetchStatus();
    // A synchronously-settled probe has already run finalize() (which cleared
    // the slot, or handed it to a queued follow-up probe): only park a probe
    // that is still open, and never overwrite what finalize() left behind.
    if (!probe.closed) {
      this.inflight = probe;
    }
  }

  /** A refresh() arrived while a probe was in flight; run it once that probe settles. */
  private refreshQueued = false;

  private cancelInflight(): void {
    // A cancelled probe's queued follow-up belongs to the old principal/cycle.
    this.refreshQueued = false;
    this.inflight?.unsubscribe();
    this.inflight = null;
  }

  private fetchStatus(): Subscription {
    return this.copilotApi
      .copilotStatus()
      .pipe(
        retry({
          count: STATUS_RETRY_COUNT,
          delay: (error: unknown, retryCount) => {
            const status = (error as { status?: number } | null)?.status;
            if (status !== 0 && (status === undefined || status < 500)) {
              return throwError(() => error);
            }
            return timer(STATUS_RETRY_BASE_DELAY_MS * 2 ** (retryCount - 1));
          },
        }),
        catchError((error: unknown) => {
          const status = (error as { status?: number } | null)?.status;
          this.logger.warn('copilot status fetch failed', {
            component: 'CopilotStatusService',
            status,
            hadPriorSuccess: this.hadSuccessForPrincipal,
          });
          // GAP-CP-021-A — degrade, don't flap: before the first success the
          // surface stays fail-closed (DARK); after one, the last-known status
          // is retained and the muted "Copilot unavailable" hint raises.
          this.unavailableSig.set(true);
          if (!this.hadSuccessForPrincipal) {
            this.statusSig.set(DARK);
          }
          return of(null);
        }),
        // RF-QA-171 — release the single-flight slot ONCE, when the whole probe
        // (including its retries) settles or is cancelled. Placed after retry
        // on purpose: before it, finalize ran on every inner teardown, freeing
        // the slot during the retry backoff so a de-auth could not cancel the
        // retrying probe and refresh() could start a second one.
        finalize(() => {
          this.inflight = null;
          if (this.refreshQueued) {
            this.refreshQueued = false;
            // Runs after the subscriber's next/complete handlers have applied
            // this probe's answer; refresh() is a no-op once de-authenticated.
            this.refresh();
          }
        }),
      )
      .subscribe((body) => {
        if (body === null) {
          return; // failure path already handled in catchError
        }
        // Only an explicit boolean `true` opens the surface — a malformed body
        // (missing / non-boolean `enabled`) is treated as dark.
        const enabled = body?.enabled === true;
        const model = typeof body?.model === 'string' ? body.model : undefined;
        // §18 budget fields — additive + optional; non-numeric values are
        // dropped so the footer bar simply hides (never renders garbage).
        const dailyBudgetTokens =
          typeof body?.dailyBudgetTokens === 'number' && Number.isFinite(body.dailyBudgetTokens)
            ? body.dailyBudgetTokens
            : undefined;
        const usedTokens =
          typeof body?.usedTokens === 'number' && Number.isFinite(body.usedTokens)
            ? body.usedTokens
            : undefined;
        this.hadSuccessForPrincipal = true;
        this.unavailableSig.set(false);
        this.statusSig.set(
          enabled
            ? {
                enabled: true,
                model,
                ...(dailyBudgetTokens !== undefined ? { dailyBudgetTokens } : {}),
                ...(usedTokens !== undefined ? { usedTokens } : {}),
              }
            : DARK,
        );
      });
  }
}
